import React from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	row: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: 'transparent',
		textAlign: 'left',
		// Picks up whichever corner radius `PressableGroup` gave this row's wrapper, the same way
		// `PersonRow` does, so the hover background follows the group's outer corners.
		borderRadius: 'inherit',
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const i18n = defineMessages({
	showMore: {
		id: 'byline-connections.preview-panel.show-more',
		defaultMessage: 'Show more',
		description:
			'Label of the last row of a Teamwork Graph panel section, such as People & Agents or Related content. Pressing it lists the remaining items of that section, which are hidden until then.',
	},
	showLess: {
		id: 'byline-connections.preview-panel.show-less',
		defaultMessage: 'Show less',
		description:
			'Label of the last row of a Teamwork Graph panel section, such as People & Agents or Related content, once it has been expanded. Pressing it hides the items it revealed, leaving only the first few.',
	},
});

export type ShowMoreRowProps = {
	/** Whether the rows this row discloses are currently shown. */
	isExpanded: boolean;
	onClick: () => void;
	testId?: string;
};

/**
 * The last row of a `PressableGroup`: the press that shows the rest of the group's rows, and
 * hides them again. Sits inside the group rather than beside it so it shares the card's shape
 * and divider, as the design has it.
 *
 * Presentational — the caller owns the expanded state and the cap it applies, since how many rows
 * are hidden differs by section. The wording is the row's own: every section says "Show more" and
 * "Show less", so one pair of strings serves them all.
 */
export const ShowMoreRow: FC<ShowMoreRowProps> = ({ isExpanded, onClick, testId }) => {
	const { formatMessage } = useIntl();

	return (
		<Pressable xcss={styles.row} aria-expanded={isExpanded} onClick={onClick} testId={testId}>
			{formatMessage(isExpanded ? i18n.showLess : i18n.showMore)}
		</Pressable>
	);
};
